import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { useSettings } from "../lib/settings";
import { Header, Footer } from "../components/Chrome";

/**
 * Retailer portal — three distinct states:
 *   - status === "approved" → full portal (browse catalog, cart, orders)
 *   - status === "pending"  → pending-only screen; no other portal features
 *   - status === "rejected" → rejection screen; no other portal features
 */
export function PortalHome() {
  const { user, logout } = useAuth();
  const { settings } = useSettings();
  const nav = useNavigate();
  if (!user) return null;
  const status = user.status || "pending";
  const canOrder = settings?.ordering_enabled && status === "approved";

  const logoutBtn = (
    <button onClick={() => { logout(); nav("/"); }} className="btn-ghost" data-testid="portal-logout">Log out</button>
  );

  // ---------------- Rejected ----------------
  if (status === "rejected") {
    return (
      <div className="min-h-screen bg-paper text-ink">
        <Header />
        <div className="pt-28 md:pt-32 pb-16 px-6 md:px-12 lg:px-16" data-testid="portal-rejected">
          <div className="overline text-black/50 mb-6">Retailer Portal</div>
          <h1 className="font-display text-5xl md:text-7xl uppercase leading-[0.9]">
            Application not approved.
          </h1>
          <div className="mt-8 border border-soda hard-shadow p-6 md:p-10 max-w-3xl bg-paper">
            <div className="overline text-soda mb-3">Account status · Rejected</div>
            <p className="text-lg leading-relaxed">
              Hi {user.contact_name || user.email}, we're sorry — we were unable to approve your application for
              <strong> {user.business_name || "your business"}</strong> at this time. If you believe this is a
              mistake or would like to appeal, please email us at
              <strong> {settings?.public_email || "orders@gtawholesalesupply.ca"}</strong> and reference your
              business name in the subject line.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/contact" className="btn-primary" data-testid="portal-rejected-contact">Contact us</Link>
              {logoutBtn}
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // ---------------- Pending ----------------
  if (status === "pending") {
    return (
      <div className="min-h-screen bg-paper text-ink">
        <Header />
        <div className="pt-28 md:pt-32 pb-16 px-6 md:px-12 lg:px-16" data-testid="portal-pending">
          <div className="overline text-black/50 mb-6">Retailer Portal</div>
          <h1 className="font-display text-5xl md:text-7xl uppercase leading-[0.9]">
            Almost there.
          </h1>
          <div className="mt-8 border border-ink hard-shadow p-6 md:p-10 max-w-3xl bg-paper">
            <div className="overline text-safety mb-3">Account status · Pending review</div>
            <p className="text-lg leading-relaxed">
              Thanks {user.contact_name}. Your application for <strong>{user.business_name}</strong> is under
              review. We'll email you the moment it's approved — typically within one business day.
            </p>
            <p className="mt-4 text-sm text-black/60">
              You'll only be able to place orders and see wholesale pricing once your account is approved. In the
              meantime feel free to browse the public catalog.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/catalog" className="btn-primary" data-testid="portal-pending-catalog">Browse catalog</Link>
              {logoutBtn}
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // ---------------- Approved ----------------
  return (
    <div className="min-h-screen bg-paper text-ink">
      <Header />
      <div className="pt-28 md:pt-32 pb-16 px-6 md:px-12 lg:px-16" data-testid="portal-home">
        <div className="overline text-black/50 mb-6">Retailer Portal</div>
        <h1 className="font-display text-5xl md:text-7xl uppercase leading-[0.9]">
          Welcome back.
        </h1>
        <div className="mt-8 border border-ink hard-shadow p-6 md:p-10 max-w-3xl bg-paper">
          <div className="overline text-cobalt mb-3">Account status · Approved</div>
          <p className="text-lg leading-relaxed mb-3">
            Hi {user.contact_name}. Your account is active. {canOrder
              ? "Browse the catalog, build a cart, and submit wholesale orders below."
              : "Online wholesale ordering is temporarily disabled while we finalise launch — please browse the catalog and reach out for pricing and orders."}
          </p>
          <ul className="text-sm text-black/70 list-disc pl-5 space-y-1">
            <li>Browse the full catalogue with wholesale pricing</li>
            {canOrder ? (
              <>
                <li>Add cases to your cart and check out with Cash or E-Transfer / Cheque</li>
                <li>Review, reprint, or cancel pending orders anytime</li>
              </>
            ) : (
              <>
                <li>Send an enquiry with what you'd like to stock</li>
                <li>Call or email us for immediate wholesale quotes</li>
              </>
            )}
          </ul>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link to="/catalog" className="btn-primary" data-testid="portal-catalog">Browse catalog</Link>
            {canOrder && <Link to="/cart" className="btn-ghost" data-testid="portal-cart">View cart</Link>}
            {canOrder && <Link to="/portal/orders" className="btn-ghost" data-testid="portal-orders">My orders</Link>}
            <Link to="/enquiry" className="btn-ghost" data-testid="portal-enquiry">Send enquiry</Link>
            {logoutBtn}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}

export default PortalHome;
